import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingBag, Users,
  RotateCcw, BarChart2, LogOut, Menu, X, ChevronRight,
  Settings, Bell
} from 'lucide-react';

const S = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0f0d0a',
    fontFamily: "'Cormorant Garamond', Georgia, serif",
  },
  sidebar: (collapsed) => ({
    width: collapsed ? 64 : 240,
    background: '#13110d',
    borderRight: '1px solid rgba(186,130,69,0.12)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.25s ease',
    flexShrink: 0,
    position: 'relative',
    zIndex: 10,
  }),
  sidebarHeader: {
    padding: '20px 16px',
    borderBottom: '1px solid rgba(186,130,69,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minHeight: 64,
  },
  logo: {
    color: '#ba8245',
    fontSize: 13,
    letterSpacing: '0.4em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    fontWeight: 600,
  },
  nav: {
    flex: 1,
    padding: '12px 8px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  navSection: {
    fontSize: 9,
    letterSpacing: '0.4em',
    color: 'rgba(245,240,232,0.2)',
    textTransform: 'uppercase',
    padding: '12px 10px 6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  sidebarFooter: {
    padding: '12px 8px',
    borderTop: '1px solid rgba(186,130,69,0.08)',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topbar: {
    height: 64,
    background: '#13110d',
    borderBottom: '1px solid rgba(186,130,69,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: 24,
  },
};

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard', section: 'Overview' },
  { label: 'Products', icon: Package, path: '/admin/products', section: 'Catalogue' },
  { label: 'Orders', icon: ShoppingBag, path: '/admin/orders', section: null },
  { label: 'Returns', icon: RotateCcw, path: '/admin/returns', section: null },
  { label: 'Customers', icon: Users, path: '/admin/customers', section: 'People' },
  { label: 'Analytics', icon: BarChart2, path: '/admin/analytics', section: 'Insights' },
];

const NavItem = ({ item, collapsed }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 10px',
        borderRadius: 6,
        textDecoration: 'none',
        color: isActive ? '#ba8245' : 'rgba(245,240,232,0.5)',
        background: isActive ? 'rgba(186,130,69,0.1)' : 'transparent',
        marginBottom: 2,
        transition: 'all 0.15s',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontSize: 14,
        letterSpacing: '0.03em',
        borderLeft: isActive ? '2px solid #ba8245' : '2px solid transparent',
      })}
      onMouseEnter={e => {
        const link = e.currentTarget;
        if (!link.classList.contains('active')) {
          link.style.color = 'rgba(245,240,232,0.85)';
          link.style.background = 'rgba(255,255,255,0.04)';
        }
      }}
      onMouseLeave={e => {
        const link = e.currentTarget;
        if (!link.style.background.includes('186,130,69')) {
          link.style.color = 'rgba(245,240,232,0.5)';
          link.style.background = 'transparent';
        }
      }}
    >
      <Icon size={16} style={{ flexShrink: 0 }} />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
};

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('siyaanish_admin');
    navigate('/admin/login');
  };

  let lastSection = null;

  return (
    <div style={S.layout}>
      {/* Sidebar */}
      <div style={S.sidebar(collapsed)}>
        <div style={S.sidebarHeader}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ba8245', padding: 0, flexShrink: 0 }}
          >
            {collapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
          {!collapsed && <span style={S.logo}>Siyaanish</span>}
        </div>

        <nav style={S.nav}>
          {navItems.map((item) => {
            const showSection = !collapsed && item.section && item.section !== lastSection;
            if (item.section) lastSection = item.section;
            return (
              <React.Fragment key={item.path}>
                {showSection && <div style={S.navSection}>{item.section}</div>}
                <NavItem item={item} collapsed={collapsed} />
              </React.Fragment>
            );
          })}
        </nav>

        <div style={S.sidebarFooter}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 10px',
              borderRadius: 6,
              background: 'none',
              border: 'none',
              color: 'rgba(245,240,232,0.4)',
              cursor: 'pointer',
              width: '100%',
              fontSize: 14,
              letterSpacing: '0.03em',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#fca5a5'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(245,240,232,0.4)'}
          >
            <LogOut size={16} style={{ flexShrink: 0 }} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={S.main}>
        <div style={S.topbar}>
          <div style={{ color: 'rgba(245,240,232,0.3)', fontSize: 13, letterSpacing: '0.05em' }}>
            Admin Panel
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(186,130,69,0.15)',
              border: '1px solid rgba(186,130,69,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#ba8245', fontSize: 12, fontWeight: 600,
            }}>
              M
            </div>
          </div>
        </div>

        <div style={S.content}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
